import { useTranslations } from '@vocab/react';
import {
  Actions,
  Button,
  Stack,
  TextField,
  useToast,
} from 'braid-design-system';
import translations from './.vocab';
import { useState, type FC } from 'react';
import { isPast, parse } from 'date-fns';

type FormState =
  | { state: 'loading' }
  | { state: 'error'; errorMessage: string }
  | { state: 'default' };

interface UpdateExpiryDateFormProps {
  onSubmitSuccess: () => void;
  jobExpireDate: string;
  updateExpiryDate: (expiryDate: Date) => Promise<boolean>;
}

export const UpdateExpiryDateForm: FC<UpdateExpiryDateFormProps> = ({
  onSubmitSuccess,
  jobExpireDate,
  updateExpiryDate,
}) => {
  const { t } = useTranslations(translations);
  const { getValidationExpiryDateMessage } = useValidateExpiryDate();
  const showToast = useToast();
  const [formState, setFormState] = useState<FormState>({ state: 'default' });
  const [expireDate, setExpireDate] = useState(jobExpireDate);

  const onUpdateExpiryDate = async () => {
    setFormState({ state: 'loading' });

    const validationResult = getValidationExpiryDateMessage(expireDate);

    if (!validationResult.valid) {
      setFormState({
        state: 'error',
        errorMessage: validationResult.errorMessage,
      });
    } else {
      const success = await updateExpiryDate(validationResult.expiredDate);

      if (success) {
        setFormState({ state: 'default' });
        onSubmitSuccess();
        showToast({
          message: t(
            'Expiry date of expiryDate has been successfully updated',
            {
              expiryDate: expireDate,
            },
          ),
          tone: 'positive',
        });
      } else {
        setFormState({
          state: 'error',
          errorMessage: t(
            'Sorry, we couldn’t save your changes. Can we try again?',
          ),
        });
      }
    }
  };

  return (
    <Stack space="small">
      <TextField
        id="expiry-date"
        label={t('(CS) Expire ad on')}
        value={expireDate}
        tone={formState.state === 'error' ? 'critical' : 'neutral'}
        onChange={(e) => setExpireDate((e.target as HTMLInputElement).value)}
        message={formState.state === 'error' ? formState.errorMessage : ''}
        reserveMessageSpace
      />

      <Actions>
        <Button
          id="form-update-expiry-date-submit"
          type="submit"
          onClick={onUpdateExpiryDate}
          loading={formState.state === 'loading'}
          tone="formAccent"
          variant="solid"
        >
          {formState.state === 'loading'
            ? t('Updating')
            : t('Update expiry date')}
        </Button>
      </Actions>
    </Stack>
  );
};

const useValidateExpiryDate = () => {
  const { t } = useTranslations(translations);
  return {
    getValidationExpiryDateMessage: (
      expiryDateString: string,
    ):
      | {
          valid: true;
          expiredDate: Date;
        }
      | {
          valid: false;
          errorMessage: string;
        } => {
      const expiredDate = parse(
        `${expiryDateString} +11 23:59:59`,
        t('dd/MM/yyyy X kk:mm:ss'),
        new Date(),
      );

      if (isNaN(expiredDate.getTime())) {
        return {
          valid: false,
          errorMessage: t('Enter the expiry date as DD/MM/YYYY'),
        };
      }

      if (isPast(expiredDate)) {
        return {
          valid: false,
          errorMessage: t('Expiry date cannot be in the past'),
        };
      }

      return {
        expiredDate,
        valid: true,
      };
    },
  };
};
